import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PuzzlePiece, Plus, CaretRight, Receipt, Tray, ArrowsClockwise, PhoneCall, Gear } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { ModuleDialog } from "../../components/platform/bespoke/ModuleDialog";
import { ChargesTable } from "../../components/platform/bespoke/ChargesTable";
import { RequestsBoard, LogRequestDialog } from "../../components/platform/bespoke/RequestsBoard";
import { InvoiceSettingsDialog } from "../../components/platform/bespoke/InvoiceSettingsDialog";
import { bespokeIcon, money, MODULE_STATUS } from "../../lib/bespoke";

function Stat({ label, value, hint, testId, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold font-[Chivo] mt-1 ${tone || ""}`}>{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function ModuleCard({ m }) {
  const Icon = bespokeIcon(m.icon);
  const st = MODULE_STATUS[m.status] || MODULE_STATUS.draft;
  return (
    <Link to={`/platform/bespoke/${m.id}`} className="rounded-xl border border-border bg-card p-4 flex gap-3 hover:border-primary/40 transition-colors group" data-testid={`bespoke-module-card-${m.key}`}>
      <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Icon size={22} weight="duotone" className="text-primary" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold truncate">{m.name}</h3>
          <Badge className={`text-[9px] ${st.cls}`} data-testid={`bespoke-module-status-${m.key}`}>{st.label}</Badge>
        </div>
        <p className="text-[10px] font-mono text-muted-foreground">/modules/{m.key}</p>
        {m.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.description}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
          <span data-testid={`bespoke-module-tenants-${m.key}`}><b>{m.tenants}</b> tenant{m.tenants === 1 ? "" : "s"}</span>
          <span>{money(m.one_off_fee)} set-up · {money(m.monthly_fee)}/month</span>
          {m.due > 0 && <span className="text-amber-700 dark:text-amber-300">{money(m.due)} due</span>}
          {m.paid > 0 && <span className="text-emerald-700 dark:text-emerald-300">{money(m.paid)} paid</span>}
        </div>
      </div>
      <CaretRight size={16} className="text-muted-foreground self-center group-hover:text-primary" />
    </Link>
  );
}

export default function BespokeModulesPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "modules";
  const [summary, setSummary] = useState(null);
  const [modules, setModules] = useState([]);
  const [ledger, setLedger] = useState({ charges: [], totals: {} });
  const [requests, setRequests] = useState([]);
  const [chargeFilter, setChargeFilter] = useState("due");
  const [create, setCreate] = useState(false);
  const [buildFrom, setBuildFrom] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [invSettingsOpen, setInvSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, m, c, r] = await Promise.all([API.get("/platform/bespoke/summary"), API.get("/platform/bespoke/modules"), API.get("/platform/bespoke/charges"), API.get("/platform/bespoke/requests")]);
      setSummary(s.data); setModules(m.data); setLedger(c.data); setRequests(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load bespoke modules");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setTab = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; });
  const accrue = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/platform/bespoke/accrue/run-now");
      toast.success(`${data.created} monthly charge${data.created === 1 ? "" : "s"} raised (${money(data.total)})${data.notified ? " · summary sent to platform admins" : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run the monthly charges"); } finally { setBusy(false); }
  };

  const tenantFilter = params.get("tenant") || "";
  const shownCharges = ledger.charges.filter((c) => (!tenantFilter || c.tenant_id === tenantFilter) && (chargeFilter === "all" || (chargeFilter === "overdue" ? c.overdue : c.status === chargeFilter)));
  const tenantFilterName = tenantFilter ? (ledger.charges.find((c) => c.tenant_id === tenantFilter)?.tenant_name || "this tenant") : "";
  const t = summary?.totals || {};

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="bespoke-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] flex items-center gap-2"><PuzzlePiece size={26} weight="duotone" className="text-primary" /> Bespoke Modules</h1>
          <p className="text-sm text-muted-foreground mt-1">Functionality built for one customer. Create the module, add it to the tenant(s) who pay for it, and track the one-off and monthly fees you invoice.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setLogOpen(true)} data-testid="bespoke-log-request-btn"><PhoneCall size={14} className="mr-1" /> Log a request</Button>
          <Button size="sm" onClick={() => setCreate(true)} data-testid="bespoke-new-module-btn"><Plus size={14} className="mr-1" /> New module</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Modules" value={summary ? summary.modules : "…"} hint={summary ? `${summary.live_modules} live` : ""} testId="bespoke-stat-modules" />
        <Stat label="Tenants using them" value={summary ? summary.tenants : "…"} hint={summary ? `${summary.active_assignments} module slot${summary.active_assignments === 1 ? "" : "s"}` : ""} testId="bespoke-stat-tenants" />
        <Stat label="Monthly recurring" value={summary ? money(summary.monthly_recurring) : "…"} hint="invoiced each month" testId="bespoke-stat-mrr" />
        <Stat label="To invoice" value={summary ? money(t.due) : "…"} hint={t.overdue > 0 ? `${money(t.overdue)} overdue` : `${t.count_due || 0} charge${t.count_due === 1 ? "" : "s"} due`} tone={t.overdue > 0 ? "text-red-600" : ""} testId="bespoke-stat-due" />
        <Stat label="Open requests" value={summary ? summary.open_requests : "…"} hint={summary?.new_requests ? `${summary.new_requests} new` : "nothing new"} tone={summary?.new_requests ? "text-sky-600" : ""} testId="bespoke-stat-requests" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="modules" data-testid="bespoke-tab-modules"><PuzzlePiece size={14} className="mr-1" /> Modules ({modules.length})</TabsTrigger>
          <TabsTrigger value="billing" data-testid="bespoke-tab-billing"><Receipt size={14} className="mr-1" /> Billing{t.count_due ? ` (${t.count_due} due)` : ""}</TabsTrigger>
          <TabsTrigger value="requests" data-testid="bespoke-tab-requests"><Tray size={14} className="mr-1" /> Requests{summary?.open_requests ? ` (${summary.open_requests})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="modules" className="mt-4">
          {modules.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="bespoke-modules-empty">
              <PuzzlePiece size={36} className="mx-auto text-muted-foreground" />
              <p className="font-semibold mt-3">No bespoke modules yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create the first one — give it a name and key, set the default fees, then add it to the customer who asked.</p>
              <Button size="sm" className="mt-4" onClick={() => setCreate(true)} data-testid="bespoke-empty-new-btn"><Plus size={14} className="mr-1" /> New module</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3" data-testid="bespoke-modules-grid">{modules.map((m) => <ModuleCard key={m.id} m={m} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="billing" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {[["due", "Due"], ["overdue", "Overdue"], ["paid", "Paid"], ["waived", "Waived"], ["all", "All"]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setChargeFilter(k)} className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${chargeFilter === k ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-muted"}`} data-testid={`bespoke-charges-filter-${k}`}>{label}</button>
            ))}
            {tenantFilter && <button type="button" onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.delete("tenant"); return n; })} className="rounded-full border border-violet-300 bg-violet-50 dark:bg-violet-400/10 px-2.5 py-1 text-xs text-violet-800 dark:text-violet-100" data-testid="bespoke-charges-tenant-filter">{tenantFilterName} · clear ×</button>}
            <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
              <span data-testid="bespoke-ledger-totals">Due {money(ledger.totals.due)} · Paid this month {money(ledger.totals.paid_this_month)}</span>
              <Button variant="outline" size="sm" onClick={() => setInvSettingsOpen(true)} data-testid="bespoke-invoice-settings-btn"><Gear size={14} className="mr-1" /> Invoice settings</Button>
              <Button variant="outline" size="sm" onClick={accrue} disabled={busy} data-testid="bespoke-accrue-btn"><ArrowsClockwise size={14} className="mr-1" /> Raise this month's charges</Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Monthly charges are raised automatically on the 1st (and the morning after a tenant is added mid-month). Everything here is billed by invoice — open the PDF or e-mail it to the tenant from each row, then mark the line paid once the money lands.</p>
          <ChargesTable charges={shownCharges} onChanged={load} />
          <InvoiceSettingsDialog open={invSettingsOpen} onOpenChange={setInvSettingsOpen} />
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <RequestsBoard requests={requests} onChanged={load} onBuild={(r) => setBuildFrom(r)} />
        </TabsContent>
      </Tabs>

      <ModuleDialog open={create || !!buildFrom} onOpenChange={(o) => { if (!o) { setCreate(false); setBuildFrom(null); } }} fromRequest={buildFrom} roles={summary?.roles} onSaved={load} />
      <LogRequestDialog open={logOpen} onOpenChange={setLogOpen} onSaved={load} />
    </div>
  );
}
